import React, { createContext, useContext } from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { mergeAttributes, Node, NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import Button from '~/components/core/Atomic/Buttons/Button';

export const CONDITIONAL_SECTION_NODE = 'conditionalSection';
export const SECTION_KEY_ATTRIBUTE = 'data-section-key';

/**
 * What the editor's owner knows about its conditional sections. The HTML holds only each section's key;
 * the label and the condition live with the owner (a template form keeps them in its `sections` list).
 */
export interface ConditionalSectionsContextValue {
  getLabel: (key: string) => string | undefined;
  /** False hides the toolbar button and the chip's edit actions. The text of a section stays editable. */
  canEditConditions: boolean;
  /** A new section was wrapped: ask for its condition. */
  onCreate: (key: string) => void;
  onEdit: (key: string) => void;
  /** The wrapper was removed and its text kept. */
  onRemove: (key: string) => void;
}

export const ConditionalSectionsContext = createContext<ConditionalSectionsContextValue | null>(null);

export const useConditionalSections = (): ConditionalSectionsContextValue | null =>
  useContext(ConditionalSectionsContext);

const ConditionalSectionView: React.FC<NodeViewProps> = ({ node, editor, getPos }) => {
  const context = useConditionalSections();
  const key = node.attrs.sectionKey as string;
  const label = context?.getLabel(key);

  const removeCondition = () => {
    if (typeof getPos !== 'function') {
      return;
    }
    const pos = getPos();
    editor.view.dispatch(editor.state.tr.replaceWith(pos, pos + node.nodeSize, node.content));
    context?.onRemove(key);
  };

  return (
    <NodeViewWrapper className="my-8 rounded border-2 border-dashed border-blue-500 px-8 pb-4">
      <div contentEditable={false} className="flex select-none items-center gap-8 py-4 text-xs text-blue-700">
        <span className="rounded-full bg-blue-100 px-8 py-2 font-semibold">
          {label ? `Shown if: ${label}` : context?.canEditConditions ? 'No condition yet' : 'Conditional section'}
        </span>
        {editor.isEditable && context?.canEditConditions ? (
          <>
            <Button variant="text" color="primary" size="small" onClick={() => context.onEdit(key)}>
              Edit condition
            </Button>
            <Button variant="text" size="small" onClick={removeCondition}>
              Remove condition
            </Button>
          </>
        ) : null}
      </div>
      <NodeViewContent />
    </NodeViewWrapper>
  );
};

/** A block of the body that renders only when its condition matches. Stored as `<div data-section-key="<ULID>">`. */
export const ConditionalSection = Node.create({
  name: CONDITIONAL_SECTION_NODE,
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      sectionKey: {
        default: null,
        parseHTML: (element) => element.getAttribute(SECTION_KEY_ATTRIBUTE),
        renderHTML: (attributes) => ({ [SECTION_KEY_ATTRIBUTE]: attributes.sectionKey }),
      },
    };
  },

  parseHTML() {
    // Above the Paragraph extension, which claims every `div`.
    return [{ tag: `div[${SECTION_KEY_ATTRIBUTE}]`, priority: 1000 }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ConditionalSectionView);
  },
});
